import { Map, List, Set } from 'immutable'
import { getConfigDataServer } from '../config'
import { isLoggedIn } from '../auth'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { isPageReady } from '../pages'
import { SET_LOCATION_DATA } from '../location/redux/actions'
import { SET_PAGE_READY } from '../pages/redux/actions'
import { createViewRecords, findView } from '../data'
import { MEDIA_STORE_KEY } from '../media'
import {
  GET_DATA_LIST,
  PERSIST_DATA,
  getDataList as getDataListAction,
  setDataList as setDataListAction,
} from '../data/redux/actions'
import {
  upsertMediaAsset,
  deleteMediaAsset,
  createMediaAssetIngestRequest,
  createMediaAssetCreateViewOptions,
  getMediaAssetIngestJobsList,
  getMediaAssetIngestUrlList,
  getMediaAssetList,
  MEDIA_ASSETS_STORE_KEY,
  MEDIA_ASSETS_INGEST_JOBS_STORE_KEY,
  MEDIA_ASSETS_INGEST_URLS_STORE_KEY,
  isMediaAssetCreatePage,
} from '.'

/**
 * Returns true if the action has a storeKey of 'mediaAssets'
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isMediaAssetsDataAction (options = {}) {
  const { action = {} } = options
  const { payload: { storeKey } = {} } = action
  return storeKey === MEDIA_ASSETS_STORE_KEY
}

/**
 * Initialize media assets create page
 * @param {*} param0 The params
 */
export function initMediaAssetsCreatePage ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_PAGE_READY, SET_LOCATION_DATA],
    ({ dispatch, state, prevState }) => {
      const { data } = state
      const storeKey = MEDIA_ASSETS_STORE_KEY
      const options = createMediaAssetCreateViewOptions(state)
      const prevOptions = createMediaAssetCreateViewOptions(prevState)
      const view = findView({ data, storeKey, options })
      const mediaReady = view && options.equals(prevOptions)
      if (mediaReady) {
        return
      }
      if (options.get('mediaId')) {
        dispatch(getDataListAction({
          storeKey: MEDIA_STORE_KEY,
          options: Map({ mediaId: options.get('mediaId') }),
        }))
      }
    },
  ).when(({ state }) => {
    const { auth, page } = state
    return isLoggedIn(auth) && isPageReady({ page }) && isMediaAssetCreatePage(state)
  })
}

/**
 * Handle the get data list action for media assets
 * @param {*} param0 The params
 */
export function getDataListMediaAssets ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const { storeKey, options } = action.payload
    try {
      const data = await getMediaAssetList({
        auth,
        server: getConfigDataServer(config),
        mediaId: options.get('mediaId'),
      })
      const include = options.get('include') || Set()
      const mediaAssets = data.get('mediaAssets') || List()
      // Get media asset ingest information
      mediaAssets.forEach((mediaAsset) => {
        if (include.includes('media_assets_ingest_jobs')) {
          dispatch(getDataListAction({
            storeKey: MEDIA_ASSETS_INGEST_JOBS_STORE_KEY,
            options: Map({ id: mediaAsset.get('id') }),
          }))
        }
        if (include.includes('media_assets_ingest_urls')) {
          dispatch(getDataListAction({
            storeKey: MEDIA_ASSETS_INGEST_URLS_STORE_KEY,
            options: Map({ id: mediaAsset.get('id') }),
          }))
        }
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(mediaAssets),
        total: data.get('totalCount'),
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        error: e,
        processing: false,
      }))
    }
  })
    .when(({ action = {} } = {}) => {
      const { type, payload: { storeKey } = {} } = action
      return type === GET_DATA_LIST && storeKey === MEDIA_ASSETS_STORE_KEY
    })
}

/**
 * Handle the get data list action for media assets
 * @param {*} param0 The params
 */
export function getDataListMediaAssetsIngestJobList ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const { storeKey, options } = action.payload
    const id = options.get('id')
    try {
      const data = await getMediaAssetIngestJobsList({
        auth,
        server: getConfigDataServer(config),
        id,
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(List([data.set('id', id)])),
        total: data.get('jobs').size,
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        error: e,
        processing: false,
      }))
    }
  })
    .when(({ action = {} } = {}) => {
      const { type, payload: { storeKey } = {} } = action
      return type === GET_DATA_LIST && storeKey === MEDIA_ASSETS_INGEST_JOBS_STORE_KEY
    })
}

/**
 * Returns true if the action has a storeKey of 'mediaAssetIngestUrls' and
 * this is a GET_DATA_LIST action
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isGetDataListMediaAssetAction (options = {}) {
  const { action = {} } = options
  const { type, payload: { storeKey } = {} } = action
  return type === GET_DATA_LIST && storeKey === MEDIA_ASSETS_INGEST_URLS_STORE_KEY
}

/**
 * Handle the get data list action for media assets
 * @param {*} param0 The params
 */
export function getDataListMediaAssetsUrlList ({ after }) {
  return after(GET_DATA_LIST, async ({ state, action, dispatch }) => {
    const { auth, config } = state
    const { storeKey, options } = action.payload
    const id = options.get('id')
    try {
      const data = await getMediaAssetIngestUrlList({
        auth,
        server: getConfigDataServer(config),
        id,
      })
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(List([data.set('id', id)])),
        total: data.get('urls').size,
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        error: e,
        processing: false,
      }))
    }
  })
    .when(isGetDataListMediaAssetAction)
}

/**
 * Returns true if the action has a storeKey of 'mediaAssetIngestJobs' and
 * this is a PERSIST_DATA action
 * @param {Object} options The parameters
 * @param {Object} [options.action={}] A redux action
 * @param {Object} [options.action.payload] The action payload
 * @param {String} [options.action.payload.storeKey] The store key
 * @returns {Boolean} True when store key equals content
 */
function isPersistDataMediaAssetIngestJobsAction (options = {}) {
  const { action = {} } = options
  const { type, payload: { storeKey } = {} } = action
  return type === PERSIST_DATA && storeKey === MEDIA_ASSETS_INGEST_JOBS_STORE_KEY
}

/**
 * Persist data for media assets ingest job create to backend
 */
export function persistDataMediaAssetIngestRequestCreate ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options, record } = action.payload
    try {
      // const mediaIngestJobRequest = await createMediaAssetIngestRequest({
      await createMediaAssetIngestRequest({
        auth,
        server: getConfigDataServer(config),
        id: record.get('id'),
        master: Map({
          url: record.getIn(['master', 'url']),
        }),
      })
      // Get the ingest jobs again since the API response contains different data
      dispatch(getDataListAction({
        storeKey,
        options,
        processing: true,
      }))
      /** @todo enable set data list when api return the same response as get data list */
      // dispatch(setDataListAction({
      //   storeKey,
      //   options,
      //   records: createRecords(List([mediaIngestJobRequest]), 'mediaId'),
      //   total: 1,
      //   processing: false,
      // }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isPersistDataMediaAssetIngestJobsAction)
}

/**
 * Persist data for media create to backend
 */
export function persistDataMediaCreate ({ after }) {
  return after(PERSIST_DATA, async ({ state, action, dispatch }) => {
    const { config, auth } = state
    const { storeKey, options, record } = action.payload
    try {
      // const isCreate = options.get('create')
      const isDelete = options.get('delete')
      let mediaAsset
      if (!isDelete) {
        mediaAsset = await upsertMediaAsset({
          auth,
          server: getConfigDataServer(config),
          data: record,
        })
      } else {
        mediaAsset = await deleteMediaAsset({
          auth,
          server: getConfigDataServer(config),
          data: record,
        })
      }
      dispatch(setDataListAction({
        storeKey,
        options,
        records: createViewRecords(mediaAsset ? List([mediaAsset]) : List()),
        total: mediaAsset ? 1 : 0,
        processing: false,
      }))
    } catch (e) {
      dispatch(setDataListAction({
        storeKey,
        options,
        processing: false,
        error: e,
      }))
    }
  })
    .when(isMediaAssetsDataAction)
}
